import React, { useState, useEffect, useRef } from 'react';
import {Dialog,DialogTitle,DialogContent,DialogActions,Button,Box,Typography,CircularProgress,Alert,Paper,IconButton,Chip,Autocomplete,TextField,useTheme,FormControlLabel,Switch} from '@mui/material';
import {Close as CloseIcon,CheckCircle as CheckCircleIcon,Error as ErrorIcon,PlayArrow as PlayIcon,Memory as MemoryIcon} from '@mui/icons-material';
import PaperComponent from './PaperComponent';
import useIsMobile from '../hooks/useIsMobile';
import { store } from '../store/store';
import { getUser } from '../store/slices/userSlice';
import { api } from '../utils/constants';


function DeployLogsDialog({ open, onClose, onDeploy }) {
    const host = process.env.REACT_APP_HOSTURL
    const theme = useTheme();
    const isSmallScreen = useIsMobile();
    const isDarkMode = theme.palette.mode === 'dark';
    const [logs, setLogs] = useState([]);
    const [status, setStatus] = useState('idle'); // 'idle', 'deploying', 'success', 'error'
    const [isDeploying, setIsDeploying] = useState(false);
    const [restartAlerts, setRestartAlerts] = useState(false);
    const [migrations, setMigrations] = useState([]);
    const [selectedMigrations, setSelectedMigrations] = useState([]);
    const [isFetchingMigrations, setIsFetchingMigrations] = useState(false);
    const [isRunningMigrations, setIsRunningMigrations] = useState(false);
    const [isFetchingServerStats, setIsFetchingServerStats] = useState(false);
    const logsEndRef = useRef(null);

    const scrollToBottom = () => {
        logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [logs]);

    useEffect(() => {
        if (open) {
            setLogs([]);
            setStatus('idle');
            setIsDeploying(false);
            setRestartAlerts(false);
            setMigrations([]);
            setSelectedMigrations([]);
            setIsFetchingMigrations(false);
            setIsRunningMigrations(false);
            setIsFetchingServerStats(false);
        }
    }, [open]);

    const appendOptimisticDeployLogs = ({ alertsOnly, includeAlerts }) => {
        const now = () => new Date().toISOString();
        const lines = [
            { message: '📁 Changed to contracting-api directory', type: 'output' },
            { message: '📥 Pulling latest changes from git...', type: 'output' },
            { message: '✅ Git pull completed successfully', type: 'output' },
        ];

        if (!alertsOnly) {
            lines.push(
                { message: '🔄 Restarting PM2 process app...', type: 'output' },
                { message: '✅ PM2 process app restarted successfully', type: 'output' },
            );
        } else {
            lines.push({ message: '⏭️ Skipping app restart (alerts worker only)', type: 'output' });
        }

        if (includeAlerts) {
            lines.push(
                { message: '🔄 Restarting PM2 process alerts...', type: 'output' },
                { message: '✅ PM2 process alerts restarted successfully', type: 'output' },
            );
        }

        lines.push(
            { message: '💾 Saving PM2 configuration...', type: 'output' },
            { message: '✅ PM2 configuration saved successfully', type: 'output' },
            { message: '🎉 Deployment completed successfully!', type: 'success' },
        );

        setLogs((prev) => [
            ...prev,
            ...lines.map((line) => ({ ...line, timestamp: now() })),
        ]);
    };

    const runDeploy = async ({ alertsOnly = false } = {}) => {
        const includeAlerts = alertsOnly || restartAlerts;
        setIsDeploying(true);
        setStatus('deploying');
        setLogs([]);

        try {
            const state = store.getState();
            const { accessToken } = getUser(state);

            if (!accessToken) {
                setLogs(prev => [...prev, {
                    message: 'No access token found. Please log in again.',
                    type: 'error',
                    timestamp: new Date().toISOString()
                }]);
                setStatus('error');
                setIsDeploying(false);
                return;
            }

            const modeLabel = alertsOnly
                ? 'alerts worker only'
                : includeAlerts
                    ? 'app + alerts worker'
                    : 'app only';

            setLogs(prev => [...prev, {
                message: `🚀 Starting deployment (${modeLabel})...`,
                type: 'info',
                timestamp: new Date().toISOString()
            }]);

            const response = await fetch(`${host}/deploy`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    restartAlerts: includeAlerts,
                    alertsOnly,
                }),
            });

            if (!response.ok) {
                if (response.status === 403) {
                    setLogs(prev => [...prev, {
                        message: '❌ Unauthorized: Only specific users can deploy',
                        type: 'error',
                        timestamp: new Date().toISOString()
                    }]);
                } else {
                    setLogs(prev => [...prev, {
                        message: `❌ HTTP ${response.status}: ${response.statusText}`,
                        type: 'error',
                        timestamp: new Date().toISOString()
                    }]);
                }
                setStatus('error');
                setIsDeploying(false);
                return;
            }

            const data = await response.json();

            setLogs(prev => [...prev, {
                message: '✅ ' + data.message,
                type: 'success',
                timestamp: new Date(data.timestamp).toISOString()
            }]);

            appendOptimisticDeployLogs({ alertsOnly, includeAlerts });

            setStatus('success');
            setIsDeploying(false);
        } catch (error) {
            console.error('Deploy error:', error);
            setLogs(prev => [...prev, {
                message: `❌ Failed to start deployment: ${error.message}`,
                type: 'error',
                timestamp: new Date().toISOString()
            }]);
            setStatus('error');
            setIsDeploying(false);
        }
    };

    const handleDeploy = () => runDeploy({ alertsOnly: false });
    const handleDeployAlertsOnly = () => runDeploy({ alertsOnly: true });

    const handleGetLastCommit = async () => {
        try {
            // Get access token from Redux store
            const state = store.getState();
            const { accessToken } = getUser(state);
            
            if (!accessToken) {
                setLogs(prev => [...prev, { 
                    message: '❌ No access token found. Please log in again.', 
                    type: 'error',
                    timestamp: new Date().toISOString()
                }]);
                return;
            }

            // Add loading log
            setLogs(prev => [...prev, { message: '🔍 Fetching latest commit information...', type: 'info',timestamp: new Date().toISOString()}]);

            // Make GET request to last commit endpoint
            const response = await fetch(`${host}${api.lastCommit}`, {
                method: 'GET',
                headers: {'Authorization': `Bearer ${accessToken}`,'Content-Type': 'application/json'},
                credentials: 'include'
            });

            if (!response.ok) {
                if (response.status === 403) {
                    setLogs(prev => [...prev, { message: '❌ Unauthorized: Only specific users can access commit information', type: 'error',timestamp: new Date().toISOString()}]);
                } else {
                    setLogs(prev => [...prev, { message: `❌ HTTP ${response.status}: ${response.statusText}`, type: 'error',timestamp: new Date().toISOString()}]);
                }
                return;
            }

            // Parse response
            const data = await response.json();
            
            if (data.success && data.commit) {
                const { hash, author, email, date, message } = data.commit;
                
                // Add commit information to logs
                setLogs(prev => [...prev, { message: `📋 Latest Commit Information:`, type: 'info',timestamp: new Date().toISOString()}]);

                setLogs(prev => [...prev, { message: `🔗 Hash: ${hash}`, type: 'output',timestamp: new Date().toISOString()}]);

                setLogs(prev => [...prev, { message: `👤 Author: ${author} <${email}>`, type: 'output',timestamp: new Date().toISOString()}]);

                setLogs(prev => [...prev, { message: `📅 Date: ${new Date(date).toLocaleString()}`, type: 'output',timestamp: new Date().toISOString()}]);

                setLogs(prev => [...prev, { message: `💬 Message: ${message}`, type: 'output',timestamp: new Date().toISOString()}]);

                setLogs(prev => [...prev, { message: '✅ Latest commit information retrieved successfully', type: 'success',timestamp: new Date().toISOString()
                }]);
            } else {
                setLogs(prev => [...prev, { message: `❌ ${data.message || 'Failed to get commit information'}`, type: 'error',timestamp: new Date().toISOString()}]);
            }

        } catch (error) {
            console.error('Get last commit error:', error);
            setLogs(prev => [...prev, { 
                message: `❌ Failed to get commit information: ${error.message}`, 
                type: 'error',
                timestamp: new Date().toISOString()
            }]);
        }
    };

    const handleFetchMigrations = async () => {
        // Prevent multiple simultaneous fetches
        if (isFetchingMigrations) {
            return;
        }
        setIsFetchingMigrations(true);
        try {
            // Get access token from Redux store
            const state = store.getState();
            const { accessToken } = getUser(state);
            
            if (!accessToken) {
                // Add error log
                setLogs(prev => [...prev, { message: '❌ No access token found. Please log in again.', type: 'error',timestamp: new Date().toISOString()}]);
                setIsFetchingMigrations(false);
                return;
            }

            // Add loading log
            setLogs(prev => [...prev, { message: '🔍 Fetching migration files...', type: 'info',timestamp: new Date().toISOString()}]);

            // Make GET request to migrations endpoint
            const response = await fetch(`${host}${api.getMigrations}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (!response.ok) {
                if (response.status === 403) {
                    setLogs(prev => [...prev, { 
                        message: '❌ Unauthorized: Only specific users can access migration information', 
                        type: 'error',
                        timestamp: new Date().toISOString()
                    }]);
                } else {
                    setLogs(prev => [...prev, { 
                        message: `❌ HTTP ${response.status}: ${response.statusText}`, 
                        type: 'error',
                        timestamp: new Date().toISOString()
                    }]);
                }
                setIsFetchingMigrations(false);
                return;
            }

            // Parse response
            const data = await response.json();
            
            if (data.success && data.migrations) {
                setMigrations(data.migrations);
                setLogs(prev => [...prev, { 
                    message: `✅ Found ${data.count} migration file(s)`, 
                    type: 'success',
                    timestamp: new Date().toISOString()
                }]);
            } else {
                setLogs(prev => [...prev, { 
                    message: `❌ ${data.message || 'Failed to fetch migrations'}`, 
                    type: 'error',
                    timestamp: new Date().toISOString()
                }]);
            }

        } catch (error) {
            console.error('Fetch migrations error:', error);
            setLogs(prev => [...prev, { 
                message: `❌ Failed to fetch migrations: ${error.message}`, 
                type: 'error',
                timestamp: new Date().toISOString()
            }]);
        } finally {
            setIsFetchingMigrations(false);
        }
    };

    const handleRunMigrations = async () => {
        if (selectedMigrations.length === 0) {
            // Add warning log
            setLogs(prev => [...prev, { message: '⚠️ Please select at least one migration to run', type: 'error',timestamp: new Date().toISOString()}]);
            return;
        }

        // Prevent multiple simultaneous executions
        if (isRunningMigrations) {
            return;
        }

        setIsRunningMigrations(true);
        try {
            // Get access token from Redux store
            const state = store.getState();
            const { accessToken } = getUser(state);
            
            if (!accessToken) {
                // Add error log
                setLogs(prev => [...prev, { message: '❌ No access token found. Please log in again.', type: 'error',timestamp: new Date().toISOString()}]);
                setIsRunningMigrations(false);
                return;
            }

            // Add info log
            setLogs(prev => [...prev, { message: `🚀 Running ${selectedMigrations.length} migration(s)...`, type: 'info',timestamp: new Date().toISOString()}]);

            // Make POST request to run migrations endpoint
            const response = await fetch(`${host}${api.runMigrations}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    migrations: selectedMigrations
                })
            });

            if (!response.ok) {
                if (response.status === 403) {
                    setLogs(prev => [...prev, { 
                        message: '❌ Unauthorized: Only specific users can run migrations', 
                        type: 'error',
                        timestamp: new Date().toISOString()
                    }]);
                } else {
                    setLogs(prev => [...prev, { 
                        message: `❌ HTTP ${response.status}: ${response.statusText}`, 
                        type: 'error',
                        timestamp: new Date().toISOString()
                    }]);
                }
                setIsRunningMigrations(false);
                return;
            }

            // Parse response
            const data = await response.json();
            
            if (data.success && data.results) {
                // Build all log entries at once to minimize state updates
                const newLogs = [];
                
                // Process each migration result separately
                data.results.forEach((result, index) => {
                    // Add separator for each migration
                    newLogs.push({ 
                        message: `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, 
                        type: 'output',
                        timestamp: new Date().toISOString()
                    });

                    newLogs.push({ 
                        message: `📄 Migration ${index + 1}/${data.results.length}: ${result.filename}`, 
                        type: 'info',
                        timestamp: new Date().toISOString()
                    });

                    if (result.success) {
                        newLogs.push({ 
                            message: `✅ Status: SUCCESS`, 
                            type: 'success',
                            timestamp: new Date().toISOString()
                        });
                    } else {
                        newLogs.push({ 
                            message: `❌ Status: FAILED (Exit Code: ${result.exitCode})`, 
                            type: 'error',
                            timestamp: new Date().toISOString()
                        });
                    }

                    // Add stdout if available
                    if (result.stdout) {
                        const stdoutLines = result.stdout.split('\n').filter(line => line.trim());
                        stdoutLines.forEach(line => {
                            newLogs.push({ 
                                message: `  ${line}`, 
                                type: result.success ? 'output' : 'error',
                                timestamp: new Date().toISOString()
                            });
                        });
                    }

                    // Add stderr if available
                    if (result.stderr) {
                        const stderrLines = result.stderr.split('\n').filter(line => line.trim());
                        stderrLines.forEach(line => {
                            newLogs.push({ 
                                message: `  ⚠️ ${line}`, 
                                type: 'error',
                                timestamp: new Date().toISOString()
                            });
                        });
                    }

                    // Add error message if available
                    if (result.error) {
                        newLogs.push({ 
                            message: `  ❌ Error: ${result.error}`, 
                            type: 'error',
                            timestamp: new Date().toISOString()
                        });
                    }
                });

                // Add summary
                newLogs.push({ 
                    message: `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, 
                    type: 'output',
                    timestamp: new Date().toISOString()
                });

                newLogs.push({ 
                    message: `📊 Summary: ${data.summary.successful} successful, ${data.summary.failed} failed out of ${data.summary.total} total`, 
                    type: data.summary.failed === 0 ? 'success' : 'error',
                    timestamp: new Date().toISOString()
                });

                if (data.summary.failed === 0) {
                    newLogs.push({ 
                        message: '🎉 All migrations completed successfully!', 
                        type: 'success',
                        timestamp: new Date().toISOString()
                    });
                } else {
                    newLogs.push({ 
                        message: `⚠️ Some migrations failed. Please review the logs above.`, 
                        type: 'error',
                        timestamp: new Date().toISOString()
                    });
                }

                // Update state once with all logs
                setLogs(prev => [...prev, ...newLogs]);
            } else {
                setLogs(prev => [...prev, { 
                    message: `❌ ${data.message || 'Failed to run migrations'}`, 
                    type: 'error',
                    timestamp: new Date().toISOString()
                }]);
            }

        } catch (error) {
            console.error('Run migrations error:', error);
            setLogs(prev => [...prev, { 
                message: `❌ Failed to run migrations: ${error.message}`, 
                type: 'error',
                timestamp: new Date().toISOString()
            }]);
        } finally {
            setIsRunningMigrations(false);
        }
    };

    const handleGetServerStats = async () => {
        if (isFetchingServerStats) {
            return;
        }

        setIsFetchingServerStats(true);
        try {
            const state = store.getState();
            const { accessToken } = getUser(state);

            if (!accessToken) {
                setLogs(prev => [...prev, {
                    message: '❌ No access token found. Please log in again.',
                    type: 'error',
                    timestamp: new Date().toISOString()
                }]);
                return;
            }

            setLogs(prev => [...prev, {
                message: '📊 Fetching server stats...',
                type: 'info',
                timestamp: new Date().toISOString()
            }]);

            const response = await fetch(`${host}${api.serverStats}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (!response.ok) {
                if (response.status === 403) {
                    setLogs(prev => [...prev, {
                        message: '❌ Unauthorized: Only specific users can access server stats',
                        type: 'error',
                        timestamp: new Date().toISOString()
                    }]);
                } else {
                    setLogs(prev => [...prev, {
                        message: `❌ HTTP ${response.status}: ${response.statusText}`,
                        type: 'error',
                        timestamp: new Date().toISOString()
                    }]);
                }
                return;
            }

            const data = await response.json();

            if (data.success && data.stats) {
                const { hostname, platform, uptime, memory, cpu, storage, swap, pm2 } = data.stats;
                const newLogs = [];

                newLogs.push({
                    message: '📊 Server Stats:',
                    type: 'info',
                    timestamp: new Date().toISOString()
                });

                newLogs.push({
                    message: `🖥️  Host: ${hostname} (${platform})`,
                    type: 'output',
                    timestamp: new Date().toISOString()
                });

                newLogs.push({
                    message: `⏱️  Uptime: ${uptime.formatted}`,
                    type: 'output',
                    timestamp: new Date().toISOString()
                });

                newLogs.push({
                    message: `🧠 RAM: ${memory.usedFormatted} / ${memory.totalFormatted} used (${memory.usedPercent}) · ${memory.freeFormatted} free`,
                    type: 'output',
                    timestamp: new Date().toISOString()
                });

                if (swap) {
                    newLogs.push({
                        message: `💾 Swap: ${swap.usedFormatted} / ${swap.totalFormatted} used · ${swap.freeFormatted} free`,
                        type: 'output',
                        timestamp: new Date().toISOString()
                    });
                }

                newLogs.push({
                    message: `⚙️  CPU: ${cpu.cores} cores · Load ${cpu.loadAverage['1m'].toFixed(2)} / ${cpu.loadAverage['5m'].toFixed(2)} / ${cpu.loadAverage['15m'].toFixed(2)} (1m / 5m / 15m)`,
                    type: 'output',
                    timestamp: new Date().toISOString()
                });

                newLogs.push({
                    message: `🔧 Model: ${cpu.model}`,
                    type: 'output',
                    timestamp: new Date().toISOString()
                });

                if (storage) {
                    newLogs.push({
                        message: `💿 Storage (${storage.mount}): ${storage.usedFormatted} / ${storage.totalFormatted} used (${storage.usedPercent}) · ${storage.availableFormatted} available`,
                        type: 'output',
                        timestamp: new Date().toISOString()
                    });
                }

                if (Array.isArray(pm2)) {
                    newLogs.push({
                        message: `📦 PM2 Processes (${pm2.length}):`,
                        type: 'info',
                        timestamp: new Date().toISOString()
                    });

                    if (pm2.length === 0) {
                        newLogs.push({
                            message: '  No PM2 processes running',
                            type: 'output',
                            timestamp: new Date().toISOString()
                        });
                    } else {
                        pm2.forEach((proc) => {
                            newLogs.push({
                                message: `  [${proc.id}] ${proc.name} · ${proc.status} · PID ${proc.pid} · ${proc.memoryFormatted} · CPU ${proc.cpuPercent}% · Uptime ${proc.uptimeFormatted} · Restarts ${proc.restarts}`,
                                type: proc.status === 'online' ? 'output' : 'error',
                                timestamp: new Date().toISOString()
                            });

                            if (proc.script) {
                                newLogs.push({
                                    message: `    Script: ${proc.script}`,
                                    type: 'output',
                                    timestamp: new Date().toISOString()
                                });
                            }

                            if (proc.unstableRestarts > 0) {
                                newLogs.push({
                                    message: `    Unstable restarts: ${proc.unstableRestarts}`,
                                    type: 'error',
                                    timestamp: new Date().toISOString()
                                });
                            }
                        });
                    }
                } else {
                    newLogs.push({
                        message: '⚠️ PM2 process info unavailable',
                        type: 'error',
                        timestamp: new Date().toISOString()
                    });
                }

                newLogs.push({
                    message: '✅ Server stats retrieved successfully',
                    type: 'success',
                    timestamp: new Date().toISOString()
                });

                setLogs(prev => [...prev, ...newLogs]);
            } else {
                setLogs(prev => [...prev, {
                    message: `❌ ${data.message || 'Failed to get server stats'}`,
                    type: 'error',
                    timestamp: new Date().toISOString()
                }]);
            }
        } catch (error) {
            console.error('Get server stats error:', error);
            setLogs(prev => [...prev, {
                message: `❌ Failed to get server stats: ${error.message}`,
                type: 'error',
                timestamp: new Date().toISOString()
            }]);
        } finally {
            setIsFetchingServerStats(false);
        }
    };

    const handleClose = () => {
        onClose();
    };

    const getStatusIcon = () => {
        switch (status) {
            case 'deploying':
                return <CircularProgress size={20} />;
            case 'success':
                return <CheckCircleIcon color="success" />;
            case 'error':
                return <ErrorIcon color="error" />;
            default:
                return <PlayIcon />;
        }
    };

    const getStatusColor = () => {
        switch (status) {
            case 'deploying':
                return 'warning';
            case 'success':
                return 'success';
            case 'error':
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusText = () => {
        switch (status) {
            case 'deploying':
                return 'Deploying...';
            case 'success':
                return 'Deployment Successful';
            case 'error':
                return 'Deployment Failed';
            default:
                return 'Ready to Deploy';
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isSmallScreen}
            PaperComponent={isSmallScreen ? undefined : PaperComponent}
            aria-labelledby="deploy-logs-dialog-title"
            slotProps={{
                paper: {
                    sx: {
                        backgroundColor: 'background.paper',
                        borderRadius: isSmallScreen ? 0 : undefined,
                        m: isSmallScreen ? 0 : undefined,
                    }
                }
            }}
        >
            <DialogTitle 
                id="deploy-logs-dialog-title" 
                sx={{ 
                    cursor: isSmallScreen ? 'default' : 'move', 
                    backgroundColor: 'background.paper',
                    borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getStatusIcon()}
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        Server Deployment
                    </Typography>
                    <Chip 
                        label={getStatusText()} 
                        color={getStatusColor()}
                        size="small"
                        variant="outlined"
                    />
                </Box>
                <IconButton onClick={handleClose} size="small" sx={{ color: 'text.secondary' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0, backgroundColor: 'background.paper' }}>
                <Box sx={{ height: '500px', display: 'flex', flexDirection: 'column' }}>
                    {/* Migration Controls */}
                    <Box sx={{ 
                        p: 2, 
                        borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0', 
                        backgroundColor: isDarkMode ? '#1e293b' : '#fafafa' 
                    }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                            Database Migrations
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                            <Button 
                                onClick={handleFetchMigrations}
                                disabled={isFetchingMigrations || isRunningMigrations}
                                variant="outlined"
                                size="small"
                                startIcon={isFetchingMigrations ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                                sx={{ 
                                    borderRadius: 1,
                                    textTransform: 'none',
                                    minWidth: '150px'
                                }}
                            >
                                {isFetchingMigrations ? 'Fetching...' : 'Fetch Migrations'}
                            </Button>
                            
                            {migrations.length > 0 && (
                                <Autocomplete
                                    multiple
                                    options={migrations}
                                    getOptionLabel={(option) => typeof option === 'string' ? option : option.filename}
                                    value={migrations.filter(m => selectedMigrations.includes(typeof m === 'string' ? m : m.filename))}
                                    onChange={(event, newValue) => {
                                        setSelectedMigrations(newValue.map(v => typeof v === 'string' ? v : v.filename));
                                    }}
                                    disabled={isRunningMigrations || isFetchingMigrations}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Migrations"
                                            placeholder="Choose migrations to run"
                                            size="small"
                                            sx={{ 
                                                minWidth: '300px',
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: 'background.paper',
                                                    '& fieldset': {
                                                        borderColor: isDarkMode ? '#334155' : undefined,
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: isDarkMode ? '#475569' : undefined,
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: isDarkMode ? '#3b82f6' : undefined,
                                                    }
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: 'text.secondary',
                                                },
                                                '& .MuiInputBase-input': {
                                                    color: 'text.primary',
                                                }
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => {
                                        const filename = typeof option === 'string' ? option : option.filename;
                                        const created = typeof option === 'string' ? '' : new Date(option.created).toLocaleDateString();
                                        return (
                                            <Box 
                                                component="li" 
                                                {...props}
                                                sx={{
                                                    backgroundColor: 'background.paper',
                                                    '&:hover': {
                                                        backgroundColor: isDarkMode ? '#1e293b' : '#f5f5f5',
                                                    }
                                                }}
                                            >
                                                <Box>
                                                    <Typography variant="body2" sx={{ color: 'text.primary' }}>{filename}</Typography>
                                                    {created && (
                                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                            Created: {created}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        );
                                    }}
                                />
                            )}
                            
                            {selectedMigrations.length > 0 && (
                                <Button 
                                    onClick={handleRunMigrations}
                                    disabled={isRunningMigrations || isFetchingMigrations}
                                    variant="contained"
                                    size="small"
                                    startIcon={isRunningMigrations ? <CircularProgress size={16} /> : <PlayIcon />}
                                    sx={{ 
                                        background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                                        borderRadius: 1,
                                        textTransform: 'none',
                                        minWidth: '180px',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #388e3c 0%, #4caf50 100%)',
                                        }
                                    }}
                                >
                                    {isRunningMigrations ? 'Running...' : `Run Selected (${selectedMigrations.length})`}
                                </Button>
                            )}
                        </Box>
                    </Box>

                    {/* Logs Container */}
                    <Paper 
                        sx={{ 
                            flex: 1, 
                            p: 2, 
                            backgroundColor: isDarkMode ? '#0f172a' : '#1e1e1e', 
                            color: '#ffffff',
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            overflow: 'auto',
                            position: 'relative'
                        }}
                    >
                        {logs.length === 0 && !isDeploying && (
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    color: isDarkMode ? '#94a3b8' : '#666', 
                                    fontStyle: 'italic',
                                    textAlign: 'center',
                                    mt: 4
                                }}
                            >
                                Click "Start Deployment" to begin...
                            </Typography>
                        )}

                        {logs.map((log, index) => (
                            <Box 
                                key={index} 
                                sx={{ 
                                    mb: 0.5,
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1
                                }}
                            >
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        color: '#888',
                                        fontSize: '0.75rem',
                                        minWidth: '80px',
                                        flexShrink: 0
                                    }}
                                >
                                    {new Date(log.timestamp).toLocaleTimeString()}
                                </Typography>
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        color: log.type === 'error' ? '#ff6b6b' : 
                                              log.type === 'success' ? '#51cf66' :
                                              log.type === 'output' ? '#ffffff' : '#74c0fc',
                                        fontFamily: 'monospace',
                                        fontSize: '0.875rem',
                                        lineHeight: 1.4,
                                        wordBreak: 'break-word'
                                    }}
                                >
                                    {log.message}
                                </Typography>
                            </Box>
                        ))}
                        
                        {isDeploying && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                <CircularProgress size={16} />
                                <Typography variant="body2" sx={{ color: '#74c0fc' }}>
                                    Deployment in progress...
                                </Typography>
                            </Box>
                        )}
                        
                        <div ref={logsEndRef} />
                    </Paper>

                    {/* Status Alert */}
                    {status === 'success' && (
                        <Alert severity="success" sx={{ m: 2 }}>
                            Deployment completed successfully! The server has been updated.
                        </Alert>
                    )}
                    
                    {status === 'error' && (
                        <Alert severity="error" sx={{ m: 2 }}>
                            Deployment failed. Please check the logs above for details.
                        </Alert>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ 
                p: 2, 
                borderTop: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                backgroundColor: 'background.paper',
                flexWrap: 'wrap',
                gap: 1,
                justifyContent: 'flex-start',
            }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={restartAlerts}
                            onChange={(e) => setRestartAlerts(e.target.checked)}
                            disabled={isDeploying}
                            size="small"
                            color="primary"
                        />
                    }
                    label={
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Also restart alerts worker
                        </Typography>
                    }
                    sx={{ mr: 1, ml: 0.5 }}
                />

                <Button 
                    onClick={handleDeploy}
                    disabled={isDeploying}
                    variant="contained"
                    startIcon={isDeploying ? <CircularProgress size={16} /> : <PlayIcon />}
                    sx={{ 
                        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        fontFamily: '"Space Grotesk", Inter, sans-serif',
                        fontWeight: 600,
                        textTransform: 'none',
                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                            boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                            transform: 'translateY(-1px)'
                        }
                    }}
                >
                    {isDeploying ? 'Deploying...' : 'Start Deployment'}
                </Button>

                <Button
                    onClick={handleDeployAlertsOnly}
                    disabled={isDeploying}
                    variant="outlined"
                    startIcon={isDeploying ? <CircularProgress size={16} /> : <PlayIcon />}
                    sx={{
                        borderColor: '#7c4dff',
                        color: '#7c4dff',
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        fontFamily: '"Space Grotesk", Inter, sans-serif',
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': {
                            borderColor: '#7c4dff',
                            backgroundColor: '#7c4dff',
                            color: 'white',
                            transform: 'translateY(-1px)'
                        }
                    }}
                >
                    Deploy alerts worker only
                </Button>

                <Button 
                    onClick={handleGetServerStats}
                    disabled={isDeploying || isFetchingServerStats}
                    variant="outlined"
                    startIcon={isFetchingServerStats ? <CircularProgress size={16} /> : <MemoryIcon />}
                    sx={{ 
                        borderColor: '#ff9800',
                        color: '#ff9800',
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        fontFamily: '"Space Grotesk", Inter, sans-serif',
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': {
                            borderColor: '#ff9800',
                            backgroundColor: '#ff9800',
                            color: 'white',
                            transform: 'translateY(-1px)'
                        }
                    }}
                >
                    {isFetchingServerStats ? 'Fetching...' : 'Server Stats'}
                </Button>

                <Button 
                    onClick={handleGetLastCommit}
                    disabled={isDeploying}
                    variant="outlined"
                    startIcon={<CheckCircleIcon />}
                    sx={{ 
                        borderColor: '#4caf50',
                        color: '#4caf50',
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        fontFamily: '"Space Grotesk", Inter, sans-serif',
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': {
                            borderColor: '#4caf50',
                            backgroundColor: '#4caf50',
                            color: 'white',
                            transform: 'translateY(-1px)'
                        }
                    }}
                >
                    Last commit message
                </Button>
                
                <Button 
                    onClick={handleClose}
                    variant="outlined"
                    disabled={isDeploying}
                    sx={{ 
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        fontFamily: '"Space Grotesk", Inter, sans-serif',
                        fontWeight: 600,
                        textTransform: 'none'
                    }}
                >
                    {isDeploying ? 'Close After Deployment' : 'Close'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default DeployLogsDialog;
